/**
 * AICostsDashboard.js — AI & Costs tab
 *
 * Token cost tracking by feature with time-series trends.
 *
 * Layout:
 *   - 4 metric cards: Total cost, Avg cost/user/month, Total tokens, Avg tokens/request
 *   - Cost-by-feature table: Feature, Requests, Tokens, Cost, Avg tokens/query, Avg cost/request
 *   - Cost trend chart: Stacked area chart — cost per feature over time
 *
 * Data source:
 *   GET /api/admin/metrics/ai-costs?timeframe=30d
 *   (Enhanced endpoint — groups by feature, computes costs from apiPricing)
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip,
  Legend, ResponsiveContainer
} from 'recharts';
import { apiService } from '../../services/api';

// =========================================
// Constants
// =========================================

const COLORS = {
  ai_explorer:         '#3B8BD4',
  phil:                '#7F77DD',
  embedding:           '#1D9E75',
  sentiment:           '#EF9F27',
  synthesis_embedding: '#D85A30',
};

const FEATURE_LABELS = {
  // Model generations are named explicitly so a cost spike can be read against
  // the model that produced it. Updated October 6, 2026 (Sonnet 4.5 -> 5.5) and
  // October 9, 2026 (Haiku 4.5 -> 5.5); these are display labels only — the
  // model recorded per row comes from the AIUsage document, so historical rows
  // still carry the older model strings and still cost out.
  // Source of truth for which model each call uses: ckg3-backend/config/aiModels.js.
  // Phil: no Haiku call is reachable (October 27, 2026). AI Explorer keeps Haiku:
  // search-term extraction (Haiku 5.5) is live again since Phase A, October 27, 2026.
  ai_explorer:         'AI Explorer (Anthropic · Sonnet 5.5 / Haiku 5.5)',
  phil:                'Phil (Anthropic · Sonnet 5.5)',
  embedding:           'Embeddings (OpenAI · text-embedding-3-small)',
  sentiment:           'Sentiment (OpenAI · GPT-4o-mini)',
  synthesis_embedding: 'Synthesis Embed (OpenAI · text-embedding-3-small)',
};

const TIMEFRAMES = [
  { key: '7d',   label: '1W' },
  { key: '30d',  label: '1M' },
  { key: '90d',  label: '3M' },
  { key: '365d', label: '1Y' },
];

const formatDateTick = (value) => {
  const date = new Date(value + 'T00:00:00');
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

const formatCost = (value) => {
  if (value == null) return '$0.00';
  if (value < 0.01) return `$${value.toFixed(6)}`;
  if (value < 1) return `$${value.toFixed(4)}`;
  return `$${value.toFixed(2)}`;
};

const formatTokens = (value) => {
  if (value == null) return '0';
  if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}M`;
  if (value >= 1_000) return `${(value / 1_000).toFixed(1)}K`;
  return value.toString();
};

// =========================================
// Cost Trend Tooltip
// =========================================

const CostTrendTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;

  const total = payload.reduce((sum, p) => sum + (p.value || 0), 0);

  return (
    <div className="chart-tooltip">
      <p className="chart-tooltip-label">{formatDateTick(label)}</p>
      {payload.map(p => (
        <p key={p.dataKey} style={{ color: p.color }}>
          {FEATURE_LABELS[p.dataKey] || p.dataKey}: {formatCost(p.value)}
        </p>
      ))}
      <p className="chart-tooltip-total">Total: {formatCost(total)}</p>
    </div>
  );
};

// =========================================
// Main Component
// =========================================

const AICostsDashboard = () => {
  const [timeframe, setTimeframe] = useState('30d');
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // ------------------------------------------
  // Data Fetching
  // ------------------------------------------

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await apiService.admin.getAICostMetrics(timeframe);
      setData(result);
    } catch (err) {
      console.error('Error fetching AI cost metrics:', err);
      setError('Failed to load AI cost data. Please try again later.');
    } finally {
      setIsLoading(false);
    }
  }, [timeframe]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // ------------------------------------------
  // Render
  // ------------------------------------------

  if (isLoading) {
    return <div className="loading-message">Loading AI cost data…</div>;
  }

  if (error) {
    return <div className="error-message">{error}</div>;
  }

  if (!data) return null;

  const { summary, byFeature, dailyCosts } = data;

  // Determine which features have data for the trend chart
  const activeFeatures = Object.keys(FEATURE_LABELS).filter(f =>
    byFeature?.some(bf => bf.feature === f)
  );

  return (
    <div className="costs-dashboard">

      {/* ============ METRIC CARDS ============ */}
      <div className="hero-cards">
        <div className="hero-card hero-card--neutral">
          <h3>Total Cost</h3>
          <div className="hero-value">{formatCost(summary?.totalCost)}</div>
          <div className="hero-subtitle">this period</div>
        </div>

        <div className="hero-card hero-card--neutral">
          <h3>Total Requests</h3>
          <div className="hero-value">{summary?.totalRequests?.toLocaleString() || 0}</div>
          <div className="hero-subtitle">API calls</div>
        </div>

        <div className="hero-card hero-card--neutral">
          <h3>Total Tokens</h3>
          <div className="hero-value">{formatTokens(summary?.totalTokens)}</div>
          <div className="hero-subtitle">in + out</div>
        </div>

        <div className="hero-card hero-card--neutral">
          <h3>Avg Cost / Request</h3>
          <div className="hero-value">{formatCost(summary?.avgCostPerRequest)}</div>
          <div className="hero-subtitle">across all features{summary?.unknownCostRows > 0 ? ` · excludes ${summary.unknownCostRows} unknown-cost ${summary.unknownCostRows === 1 ? 'row' : 'rows'}` : ''}</div>
        </div>
      </div>

      {/* ============ TIME FILTER ============ */}
      <div className="costs-toolbar">
        <h2>Cost Breakdown</h2>
        <div className="time-filter">
          {TIMEFRAMES.map(tf => (
            <button
              key={tf.key}
              className={timeframe === tf.key ? 'active' : ''}
              onClick={() => setTimeframe(tf.key)}
            >
              {tf.label}
            </button>
          ))}
        </div>
      </div>

      {/* ============ FEATURE TABLE ============ */}
      {byFeature && byFeature.length > 0 ? (
        <div className="costs-table-container">
          <table className="costs-table">
            <thead>
              <tr>
                <th>Feature</th>
                <th className="num">Requests</th>
                <th className="num">Tokens In</th>
                <th className="num">Tokens Out</th>
                <th className="num">Cost</th>
                <th className="num">Avg Tokens/Req</th>
                <th className="num">Avg Cost/Req</th>
              </tr>
            </thead>
            <tbody>
              {byFeature.map(row => (
                <tr key={row.feature}>
                  <td>
                    <span
                      className="feature-dot"
                      style={{ backgroundColor: COLORS[row.feature] || '#999' }}
                    />
                    {FEATURE_LABELS[row.feature] || row.feature}
                  </td>
                  <td className="num">{row.requests.toLocaleString()}</td>
                  <td className="num">{formatTokens(row.tokensIn)}</td>
                  <td className="num">{formatTokens(row.tokensOut)}</td>
                  <td className="num">{formatCost(row.totalCost)}</td>
                  <td className="num">{formatTokens(row.avgTokensPerRequest)}</td>
                  <td className="num">{formatCost(row.avgCostPerRequest)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td><strong>Total</strong></td>
                <td className="num"><strong>{summary.totalRequests.toLocaleString()}</strong></td>
                <td className="num"><strong>{formatTokens(summary.totalTokensIn)}</strong></td>
                <td className="num"><strong>{formatTokens(summary.totalTokensOut)}</strong></td>
                <td className="num"><strong>{formatCost(summary.totalCost)}</strong></td>
                <td className="num">—</td>
                <td className="num"><strong>{formatCost(summary.avgCostPerRequest)}</strong></td>
              </tr>
            </tfoot>
          </table>
        </div>
      ) : (
        <div className="org-empty-state">
          No AI usage data in this period.
        </div>
      )}

      {/* ============ COST TREND CHART ============ */}
      {dailyCosts && dailyCosts.length > 0 && (
        <div className="chart-container">
          <div className="chart-header">
            <h2 className="chart-title">Cost Trend by Feature</h2>
          </div>
          <ResponsiveContainer width="100%" height={320}>
            <AreaChart data={dailyCosts}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
              <XAxis
                dataKey="date"
                tickFormatter={formatDateTick}
                tick={{ fontSize: 12, fill: '#888' }}
              />
              <YAxis
                tickFormatter={formatCost}
                tick={{ fontSize: 12, fill: '#888' }}
              />
              <Tooltip content={<CostTrendTooltip />} />
              <Legend
                formatter={(value) => FEATURE_LABELS[value] || value}
              />
              {activeFeatures.map(feature => (
                <Area
                  key={feature}
                  type="monotone"
                  dataKey={feature}
                  stackId="cost"
                  fill={COLORS[feature]}
                  stroke={COLORS[feature]}
                  fillOpacity={0.6}
                />
              ))}
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* ============ DATA NOTE ============ */}
      <div className="costs-note">
        Costs are computed from token counts using model pricing rates.
        Historical records prior to April 2026 may show estimated costs
        calculated at query time rather than recorded at ingestion.
      </div>
    </div>
  );
};

export default AICostsDashboard;
